import { useCallback, useEffect, useState } from "react";
import type Conversation from "admin/types/interfaces/conversation";
import { useDebounce } from "~/hooks/useDebounce";
import { useMutator } from "~/hooks/useFetcher";

const { VITE_CONVERSATIONS_API } = import.meta.env;

// Mirrors the ConversationTable typeahead precedent: don't query until the user
// has typed enough to be meaningful, and page in small chunks for the dropdown.
export const MIN_SEARCH_LENGTH = 3;
export const SESSION_PAGE_SIZE = 5;

// Hand-written mirror of the Python `ConversationSearchRequest`
// (conversations-types is the source of truth), narrowed to the fields the
// session search sends: the free-text content query and an optional project
// scope. `resource_id` is snake_case because request bodies are sent verbatim
// (useFetcher does not decamelize them); the backend ANDs it with `query`.
export interface SessionSearchRequest {
  query: string;
  resource_id?: string;
}

// A self-search response item (`ConversationWithMetadata`) as camelized by the
// fetcher, plus the content-search fields. `snippet`/`rank` are null on a
// metadata-only response; the home search always sends a query, so they are
// populated. `rank` is the backend field name (ts_rank relevance); it is
// carried for completeness — result ordering already follows the API's order.
export interface SessionSearchResult extends Conversation {
  snippet?: string | null;
  rank?: number | null;
}

interface PageMetadata {
  limit: number;
  offset: number;
  totalCount: number;
  hasMore: boolean;
}

interface SessionSearchResponse {
  items: SessionSearchResult[];
  metadata: PageMetadata;
}

interface UseSearchSessionsResult {
  results: SessionSearchResult[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  error: unknown;
  loadMore: () => void;
}

interface UseSearchSessionsOptions {
  limit?: number;
  // When set, restrict the search to a single project's sessions (matching the
  // conversation's `resource_id`). Omit for a global, all-projects search.
  resourceId?: string;
}

// Referentially stable empty return for the "no active search" case. A fresh
// `[]` here would change identity every render; Home feeds `results` into its
// `heroContent` useMemo, whose consumer (useNextGenHeader) can't structurally
// compare the hero's inline event handlers, so a new reference re-fires
// setHeroContent every render — an infinite update loop (React #185). Mirrors
// Home's EMPTY_PROJECTS/EMPTY_DATASETS constants.
const EMPTY_RESULTS: SessionSearchResult[] = [];

// The backend snippet wraps matched terms in <em>…</em>. Match highlighting is
// disabled for now, so strip the markup and render the snippet as plain text —
// never via dangerouslySetInnerHTML.
export const stripEmphasis = (snippet: string): string => snippet.replace(/<\/?em>/g, "");

/**
 * Content-search the caller's own conversations for the session
 * search. Debounces the query, hits the self-scoped `POST /search` (never the
 * admin endpoint), and pages by `offset`, appending each page so "Load more"
 * grows the list. Previous results stay visible while the next request is in
 * flight (keepPreviousData), so typing/paging never flashes an empty list.
 */
export const useSearchSessions = (
  query: string,
  { limit = SESSION_PAGE_SIZE, resourceId }: UseSearchSessionsOptions = {}
): UseSearchSessionsResult => {
  const trimmed = query.trim();
  const debounced = useDebounce(trimmed);
  const canSearch = debounced.length >= MIN_SEARCH_LENGTH;
  // Gate the returned results on the live (un-debounced) length so clearing the
  // input removes results immediately, ahead of the debounce settling.
  const showResults = trimmed.length >= MIN_SEARCH_LENGTH;

  // Page state keyed to the query *and* project scope it belongs to: when either
  // the debounced query or the `resourceId` changes, `page` derives back to 0
  // without an effect, so a new query — or a switch to a different project —
  // restarts from the first page instead of paging from a stale offset (which
  // would append the next project's page onto the previous project's results).
  const [pageState, setPageState] = useState<{
    query: string;
    resourceId?: string;
    page: number;
  }>({
    query: debounced,
    resourceId,
    page: 0,
  });
  const currentPage =
    pageState.query === debounced && pageState.resourceId === resourceId ? pageState.page : 0;
  const offset = currentPage * limit;

  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const url = `${VITE_CONVERSATIONS_API}/search?${params.toString()}`;

  const { trigger, isMutating, error } = useMutator<SessionSearchResponse, SessionSearchRequest>(
    url,
    {
      requestInit: { method: "POST" },
      shouldCamelize: true,
    }
  );

  const [results, setResults] = useState<SessionSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // The content search is a POST whose body (the query) varies and whose pages
  // accumulate, so it drives an imperative mutation rather than a declarative
  // useFetcher key (same shape as `useConversations`). The `active` guard drops
  // a response that resolves after the query has already moved on.
  useEffect(() => {
    if (!canSearch) {
      setResults([]);
      setTotal(0);
      setHasMore(false);
      return;
    }
    let active = true;
    trigger({
      query: debounced,
      ...(resourceId ? { resource_id: resourceId } : {}),
    }).then((response) => {
      if (!active || !response) {
        return;
      }
      setResults((prev) => (offset === 0 ? response.items : [...prev, ...response.items]));
      setTotal(response.metadata.totalCount);
      setHasMore(response.metadata.hasMore);
    });
    return () => {
      active = false;
    };
  }, [canSearch, debounced, offset, resourceId, trigger]);

  const loadMore = useCallback(() => {
    setPageState({ query: debounced, resourceId, page: currentPage + 1 });
  }, [debounced, resourceId, currentPage]);

  return {
    results: showResults ? results : EMPTY_RESULTS,
    total,
    hasMore: showResults ? hasMore : false,
    isLoading: isMutating,
    error,
    loadMore,
  };
};
